import type { FC, ReactNode } from 'react';
import { Action } from '@interstate/components/Action';
import { Alert } from '@interstate/components/Alert';
import { Grid } from '@interstate/components/Grid';
import { Modal } from '@interstate/components/Modal';
import { Typography } from '@interstate/components/Typography';

import { DEALER_SUPPORT_REQUEST_FORM } from '../../constants';
import { collapseUnusedFloatingFooterSpacerSx } from './ConfirmParticipantOperationModal.style';

export type ConfirmParticipantOperationModalProps = {
    show: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    isPending: boolean;
    hasFailed: boolean;
    id: string;
    dataTestIdPrefix: string;
    header: string;
    prompt: ReactNode;
    confirmLabel: string;
    errorTitle: string;
    errorMessage: string;
    reminder?: ReactNode;
};

export const ConfirmParticipantOperationModal: FC<ConfirmParticipantOperationModalProps> = ({
    show,
    onConfirm,
    onCancel,
    isPending,
    hasFailed,
    id,
    dataTestIdPrefix,
    header,
    prompt,
    confirmLabel,
    errorTitle,
    errorMessage,
    reminder
}) => {
    const openSupportTicketUrl = () => {
        window.open(DEALER_SUPPORT_REQUEST_FORM);
    };

    const cancelTestId = `${dataTestIdPrefix}-cancel-btn`;

    return (
        <Modal
            size="large"
            id={id}
            data-testid={id}
            show={show}
            onHide={onCancel}
            sx={collapseUnusedFloatingFooterSpacerSx}
            header={header}
            footer={{
                layout: 'rightAligned',
                ...(hasFailed
                    ? {
                          primary: {
                              action: onCancel,
                              label: 'Cancel',
                              buttonStyle: 'secondary',
                              customIdentifiers: { primaryButton: { dataTestId: cancelTestId } }
                          }
                      }
                    : {
                          options: {
                              action: onCancel,
                              label: 'Cancel',
                              buttonStyle: 'secondary',
                              disabled: isPending,
                              customIdentifiers: { optionButton0: { dataTestId: cancelTestId } }
                          },
                          primary: {
                              action: onConfirm,
                              label: confirmLabel,
                              isLoading: isPending,
                              disabled: isPending,
                              customIdentifiers: { primaryButton: { dataTestId: `${dataTestIdPrefix}-confirm-btn` } }
                          }
                      })
            }}
        >
            {hasFailed ? (
                <Grid container flexDirection="column" gap={2}>
                    <Alert type="error" role="alert" title={errorTitle} data-testid={`${dataTestIdPrefix}-error`}>
                        <Typography variant="body-sm" tag="div">
                            {errorMessage}
                        </Typography>
                    </Alert>
                    <Grid>
                        <Typography variant="strong-md" tag="span">
                            Please contact support:{' '}
                        </Typography>
                        <Typography variant="body-md" tag="span">
                            1-888-895-2994 or create a
                        </Typography>
                        <Action onClick={openSupportTicketUrl} data-testid="support-ticket" sx={{ marginLeft: '4px' }}>
                            <Typography variant="anchor-inline-md" color="sem.color.surface.button.primary.rest">
                                Support Ticket
                            </Typography>
                        </Action>
                    </Grid>
                </Grid>
            ) : (
                <Typography variant="body-md" tag="p">
                    {prompt}
                    {reminder && (
                        <>
                            {' '}
                            <span data-testid={`${dataTestIdPrefix}-reminder`}>{reminder}</span>
                        </>
                    )}
                </Typography>
            )}
        </Modal>
    );
};
